/**
 * /ai/chatgpt 展示层常量。
 * 注意事项：
 * 1. 所有可见文案统一走 i18next.t('chatgpt-xxx', '默认英文')，以兼容 SSR 与翻译上报（词库见 public/lang/local/report-chatgpt.json）。
 * 2. 操作步骤截图与能力区图走 CDN：`${cdnHost}/resources/egg/ai/chatgpt/{name}`（见 getConstants 内 img）。
 * 3. Hero 聊天预览里的商品图是真实商品图（oss-cf 外链）、价格、各类 href/icon 不属于翻译文案，保持原样。
 * 4. 翻译 key 规则参照 odm：`{pageId}-{区块}-{描述}` 全小写 kebab；i18next.t 调用须落在本文件，归属 router 的 /ai/chatgpt(pageId=chatgpt)。
 */
function getConstants(cdnHost) {
  var i18next = require('../mcpLanding/i18nText').getI18next();
  // CDN 图片路径助手：${cdnHost}/resources/egg/ai/chatgpt/{name}（cdnHost 为空时取相对路径）
  var img = function (name) {
    return (cdnHost || '') + '/resources/egg/ai/chatgpt/' + name;
  };

  var HERO = {
    badge: i18next.t('chatgpt-hero-badge', 'New MCP Server Integration'),
    title: i18next.t('chatgpt-hero-title', 'Manage Your Dropshipping Business, '),
    titleHighlight: i18next.t('chatgpt-hero-title-highlight', 'Right from Your AI Chat'),
    subtitle: i18next.t(
      'chatgpt-hero-subtitle',
      'Connect CJdropshipping to ChatGPT. Search products, sync orders, and track logistics through simple natural language.'
    ),
    primaryCta: i18next.t('chatgpt-hero-cta-primary', 'Start Using Now'),
    secondaryCta: i18next.t('chatgpt-hero-cta-secondary', 'View Documentation'),
    secondaryCtaHref: 'https://developers.cjdropshipping.com/en/api/api2/mcp.html',
    previewTitle: i18next.t('chatgpt-hero-preview-title', 'ChatGPT + CJ MCP Server'),
    userMessage: i18next.t(
      'chatgpt-hero-user-message',
      'Can you find me top-rated wireless headphones for under $30 on CJdropshipping?'
    ),
    botIntro: i18next.t(
      'chatgpt-hero-bot-intro',
      'Searching CJdropshipping inventory... I found 3 high-potential products for you:'
    ),
    botProducts: [
      {
        image:
          'https://oss-cf.cjdropshipping.com/product/2025/01/20/15/f58ff0b6-5ddf-4407-aba9-216af3c4cbff.jpg?x-oss-process=image/format,webp,image/resize,m_fill,m_pad,w_64,h_64',
        name: i18next.t(
          'chatgpt-hero-product-1-name',
          'Wireless Bluetooth Headphones, Small, Portable, And Very Practical.'
        ),
        price: '$13.31',
      },
      {
        image:
          'https://oss-cf.cjdropshipping.com/product/2024/06/16/03/6f09e640-ab70-4156-9b33-3fc3fb9e8a5d.jpg?x-oss-process=image/format,webp,image/resize,m_fill,m_pad,w_64,h_64',
        name: i18next.t(
          'chatgpt-hero-product-2-name',
          'Wireless In-ear Noise-reduction Bluetooth-compatible Headset'
        ),
        price: '$14.10',
      },
    ],
  };

  /* 推荐提问列表：展示在「Deployment Process」上方。icon 取值见 McpLanding 的 PROMPT_ICON_PATHS。 */
  var PROMPTS = {
    title: i18next.t('chatgpt-prompts-title', 'Recommended Prompts'),
    subtitle: i18next.t(
      'chatgpt-prompts-subtitle',
      'Click any prompt to copy it, then paste it into ChatGPT to call the CJ MCP server.'
    ),
    copyLabel: i18next.t('chatgpt-prompts-copy', 'Copy'),
    copiedLabel: i18next.t('chatgpt-prompts-copied', 'Copied!'),
    items: [
      { icon: 'search', text: i18next.t('chatgpt-prompt-1', 'Search for "phone case" and show the first 20 results') },
      { icon: 'tag', text: i18next.t('chatgpt-prompt-2', 'What colors and sizes are available for product CJYD2774438?') },
      { icon: 'globe', text: i18next.t('chatgpt-prompt-3', 'What warehouses does CJ have globally? Please list all locations') },
      { icon: 'truck', text: i18next.t('chatgpt-prompt-4', 'Calculate the shipping cost for sending 2 units of variant CJYD277443801AZ from China to the US') },
      { icon: 'order', text: i18next.t('chatgpt-prompt-5', 'Open my shopping cart page so I can review the items in it') },
      { icon: 'card', text: i18next.t('chatgpt-prompt-6', 'Which of my orders are still waiting for payment?') },
      { icon: 'wallet', text: i18next.t('chatgpt-prompt-7', 'How much balance do I currently have in my CJ account?') },
      { icon: 'store', text: i18next.t('chatgpt-prompt-8', 'Show me the product connection mappings between CJ and my Shopify store') },
      { icon: 'gauge', text: i18next.t('chatgpt-prompt-9', 'Check my current API call rate limit status') },
      { icon: 'alert', text: i18next.t('chatgpt-prompt-10', 'Show me all the stores connected to my CJ account') },
    ],
  };

  var CAPABILITIES = {
    title: i18next.t('chatgpt-capabilities-title', 'Intelligent Commerce '),
    titleHighlight: i18next.t('chatgpt-capabilities-title-highlight', 'At Your Fingertips'),
    subtitle: i18next.t(
      'chatgpt-capabilities-subtitle',
      'Ditch the complex dashboards. Speak to your data and let the AI handle the heavy lifting of running your store.'
    ),
    items: [
      {
        image: img('ability1.jpg'),
        title: i18next.t('chatgpt-capability-1-title', 'Product Search'),
        description: i18next.t(
          'chatgpt-capability-1-desc',
          "Instantly query millions of products from CJdropshipping's global catalog with natural language prompts."
        ),
      },
      {
        image: img('ability2.jpg'),
        title: i18next.t('chatgpt-capability-2-title', 'Order Management'),
        description: i18next.t(
          'chatgpt-capability-2-desc',
          'Check order status, update details, and sync transactions between your store and CJ without leaving the chat.'
        ),
      },
      {
        image: img('ability3.jpg'),
        title: i18next.t('chatgpt-capability-3-title', 'Logistics Tracking'),
        description: i18next.t(
          'chatgpt-capability-3-desc',
          'Real-time shipping updates and route predictions. Just ask "Where is order #1234?" and get an instant map link.'
        ),
      },
      {
        image: img('ability4.jpg'),
        title: i18next.t('chatgpt-capability-4-title', 'Inventory Insights'),
        description: i18next.t(
          'chatgpt-capability-4-desc',
          'Monitor stock levels across global warehouses and receive low-inventory alerts automatically through AI analysis.'
        ),
      },
      {
        image: img('ability5.jpg'),
        title: i18next.t('chatgpt-capability-5-title', 'AI Commerce Q&A'),
        description: i18next.t(
          'chatgpt-capability-5-desc',
          'Ask business strategy questions like "What are the trending niches for Q4?" and get data-backed answers.'
        ),
      },
      {
        image: img('ability6.jpg'),
        title: i18next.t('chatgpt-capability-6-title', 'Smart Recommendations'),
        description: i18next.t(
          'chatgpt-capability-6-desc',
          "Personalized product suggestions based on your store's sales history and global market trends."
        ),
      },
    ],
  };

  var OPERATION = {
    title: i18next.t('chatgpt-operation-title', 'Deployment Process'),
    subtitle: i18next.t(
      'chatgpt-operation-subtitle',
      'Integrate the powerful CJ MCP service into your ChatGPT environment in just six simple steps and unlock AI-driven supply chain automation.'
    ),
    steps: [
      {
        title: i18next.t('chatgpt-operation-step-1-title', 'Go to Settings'),
        label: i18next.t('chatgpt-operation-step-1-label', 'Open your ChatGPT settings'),
        image: img('chatgpt1.jpg'),
        desc: i18next.t(
          'chatgpt-operation-step-1-desc',
          'First, open your ChatGPT personal center and click the settings icon in the bottom-left corner. This is the starting point for configuring all advanced extensions and MCP servers.'
        ),
        alt: i18next.t(
          'chatgpt-operation-step-1-alt',
          'ChatGPT personal center with the settings icon highlighted in the bottom-left corner as the entry point for MCP configuration.'
        ),
      },
      {
        title: i18next.t('chatgpt-operation-step-2-title', 'Enable Developer Mode'),
        label: i18next.t('chatgpt-operation-step-2-label', 'Turn on MCP developer mode'),
        image: img('chatgpt2.jpg'),
        desc: i18next.t(
          'chatgpt-operation-step-2-desc',
          'In the settings menu, navigate to "Beta Features" or "Developer Options" and enable MCP developer mode. This step allows ChatGPT to communicate with external supply chain data interfaces.'
        ),
        alt: i18next.t(
          'chatgpt-operation-step-2-alt',
          'ChatGPT settings menu showing the Beta Features / Developer Options panel where MCP developer mode is switched on.'
        ),
      },
      {
        title: i18next.t('chatgpt-operation-step-3-title', 'Manage Enabled Apps'),
        label: i18next.t('chatgpt-operation-step-3-label', 'View active integrations'),
        image: img('chatgpt3.jpg'),
        desc: i18next.t(
          'chatgpt-operation-step-3-desc',
          'Open the "Connected Apps" or "MCP Servers" management list to view currently active external integrations. Here you can monitor the running status of your servers at any time.'
        ),
        alt: i18next.t(
          'chatgpt-operation-step-3-alt',
          'The Connected Apps / MCP Servers management list in ChatGPT displaying active external integrations and their status.'
        ),
      },
      {
        title: i18next.t('chatgpt-operation-step-4-title', 'Create New App'),
        label: i18next.t('chatgpt-operation-step-4-label', 'Add a new MCP server'),
        image: img('chatgpt4_1.jpg'),
        desc: i18next.t(
          'chatgpt-operation-step-4-desc',
          'Select "Add New Server" or "Create Application". The system will guide you through an intuitive configuration interface for mounting CJdropshipping\'s powerful feature modules. Refer to this path for the <a href="https://developers.cjdropshipping.com/en/api/api2/mcp.html#_2-chatgpt-remote-http-mode" target="_blank" rel="noopener noreferrer">server URL</a>'
        ),
        alt: i18next.t(
          'chatgpt-operation-step-4-alt',
          'ChatGPT configuration interface for adding a new server or creating an application to mount CJdropshipping features.'
        ),
      },
      {
        title: i18next.t('chatgpt-operation-step-5-title', 'Select the app'),
        label: i18next.t('chatgpt-operation-step-5-label', 'Choose the app in the input box.'),
        image: img('chatgpt5.jpg'),
        desc: i18next.t(
          'chatgpt-operation-step-5-desc',
          'Enter / and the app you just created will show up. Simply select the matching app.'
        ),
        alt: i18next.t(
          'chatgpt-operation-step-5-alt',
          'Server configuration form in ChatGPT where the CJ-provided endpoint and API key are entered.'
        ),
      },
      {
        title: i18next.t('chatgpt-operation-step-6-title', 'Start Using in Chat'),
        label: i18next.t('chatgpt-operation-step-6-label', 'Chat to call CJ MCP'),
        image: img('chatgpt6.jpg'),
        desc: i18next.t(
          'chatgpt-operation-step-6-desc',
          'Once configured, simply type commands like "find the latest trending products" in the chat. The AI will automatically call the CJ MCP server and return structured data.'
        ),
        alt: i18next.t(
          'chatgpt-operation-step-6-alt',
          'ChatGPT conversation where a natural language command triggers the CJ MCP server and returns structured product data.'
        ),
      },
    ],
  };

  var FAQ = {
    title: i18next.t('chatgpt-faq-title', 'Frequently Asked Questions'),
    subtitle: i18next.t('chatgpt-faq-subtitle', 'Find answers to common questions about our MCP server integration.'),
    items: [
      {
        question: i18next.t('chatgpt-faq-1-question', 'Is the CJ MCP server free to use?'),
        answer: i18next.t(
          'chatgpt-faq-1-answer',
          'Yes, the basic MCP server connection is free for all registered CJdropshipping users. Premium features like advanced analytics and higher API rate limits may require a professional tier subscription.'
        ),
      },
      {
        question: i18next.t('chatgpt-faq-2-question', 'Which AI models are supported?'),
        answer: i18next.t(
          'chatgpt-faq-2-answer',
          'We currently officially support ChatGPT, Claude, and any environment compatible with the Model Context Protocol (MCP) standard.'
        ),
      },
      {
        question: i18next.t('chatgpt-faq-3-question', 'How secure is my business data?'),
        answer: i18next.t(
          'chatgpt-faq-3-answer',
          "Security is our top priority. The MCP server uses encrypted tokens for authentication, and we never store your customer data or store credentials on the AI's servers—only the data required for the specific query is processed."
        ),
      },
    ],
  };

  var CTA = {
    title: i18next.t('chatgpt-cta-title', 'Ready to Automate Your Business?'),
    subtitle: i18next.t(
      'chatgpt-cta-subtitle',
      'Join thousands of smart dropshippers who are already scaling their stores with AI-driven operations.'
    ),
    docButton: i18next.t('chatgpt-cta-doc-button', 'View Documentation'),
    docHref: 'https://developers.cjdropshipping.com/en/api/api2/mcp.html#_2-chatgpt-remote-http-mode',
  };

  return { HERO: HERO, PROMPTS: PROMPTS, CAPABILITIES: CAPABILITIES, OPERATION: OPERATION, FAQ: FAQ, CTA: CTA };
}

module.exports = { getConstants: getConstants };
